import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { StepProps } from "../types";

const TagsStep: React.FC<StepProps> = ({ data, onUpdateData }) => {
  const [newTagInput, setNewTagInput] = useState("");
  const [tags, setTags] = useState<string[]>(data.tags || []);

  // Auto-generate tags based on project data
  const autoGeneratedTags = useMemo(() => {
    const generated: string[] = [];
    
    // Extract words from project name
    if (data.name) {
      const nameWords = data.name.split(/[\s\-_]+/);
      nameWords.forEach((word) => {
        const trimmed = word.trim();
        if (trimmed.length >= 3) {
          generated.push(trimmed.toLowerCase());
        }
      });
    }
    
    // Extract location-based tags from coordinates
    if (data.selectedLatLng) {
      const { lat, lng } = data.selectedLatLng;
      
      // Simple region detection based on coordinates
      if (-44 <= lat && lat <= -10 && 113 <= lng && lng <= 154) {
        generated.push("australia");
      } else if (25 <= lat && lat <= 70 && -170 <= lng && lng <= -50) {
        generated.push("north america");
      } else if (35 <= lat && lat <= 70 && -10 <= lng && lng <= 40) {
        generated.push("europe");
      } else if (10 <= lat && lat <= 55 && 60 <= lng && lng <= 150) {
        generated.push("asia");
      }
    }
    
    // Remove duplicates
    return Array.from(new Set(generated));
  }, [data.name, data.selectedLatLng]);

  // Initialize tags with auto-generated ones if not already set
  useEffect(() => {
    if (!data.tags && autoGeneratedTags.length > 0) {
      setTags(autoGeneratedTags);
      onUpdateData({ tags: autoGeneratedTags });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const handleAddTag = () => {
    if (!newTagInput.trim()) return;
    
    const trimmedTag = newTagInput.trim().toLowerCase();
    if (!tags.includes(trimmedTag)) {
      const newTags = [...tags, trimmedTag];
      setTags(newTags);
      onUpdateData({ tags: newTags });
      setNewTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    const newTags = tags.filter((tag) => tag !== tagToRemove);
    setTags(newTags);
    onUpdateData({ tags: newTags });
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleAddTag();
    }
  };

  return (
    <Box sx={{ p: 4, maxWidth: 800, mx: "auto" }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Project Tags
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Add tags to organize and filter your projects. We've automatically generated some tags based on your project name and location. You can remove any you don't want or add custom tags.
      </Typography>

      {/* Add custom tag */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter a custom tag..."
            value={newTagInput}
            onChange={(e) => setNewTagInput(e.target.value)}
            onKeyPress={handleKeyPress}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddTag}
            disabled={!newTagInput.trim()}
            sx={{
              backgroundColor: "#00d4ad",
              "&:hover": {
                backgroundColor: "#00b496",
              },
              whiteSpace: "nowrap",
            }}
          >
            Add Tag
          </Button>
        </Box>
      </Box>

      {/* Current tags */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ mb: 1, fontWeight: 600 }}>
          Selected Tags ({tags.length})
        </Typography>
        {tags.length > 0 ? (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                onDelete={() => handleRemoveTag(tag)}
                deleteIcon={
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveTag(tag)}
                    sx={{ color: "inherit" }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                }
                sx={{
                  backgroundColor: "#e0f2fe",
                  color: "#0369a1",
                  "& .MuiChip-deleteIcon": {
                    color: "#0369a1",
                  },
                }}
              />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
            No tags selected. You can add tags above or proceed without tags.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default TagsStep;

